import React, { useState, useEffect, useCallback } from 'react';
import { 
  Zap, TrendingUp, TrendingDown, AlertTriangle, 
  Target, CheckCircle, XCircle, RefreshCw, Info,
  ChevronDown, ChevronRight, Pause, Award, Wifi, 
  DollarSign, Smartphone, Calendar, MapPin, Users,
  Settings, ExternalLink, Plus, MinusCircle, RotateCcw, Lightbulb
} from 'lucide-react';
import { formatCurrency } from '../utils/formatting';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from './Toast';
import { trackOnboardingMilestone } from '../utils/onboarding';

// ============================================
// OPTEO-STYLE OPTIMIZATION ENGINE
// ============================================

// Helper function to format day names (fallback for API enum values)
const formatDayName = (day) => {
  if (!day) return day;
  const dayNames = {
    'MONDAY': 'Monday',
    'TUESDAY': 'Tuesday',
    'WEDNESDAY': 'Wednesday',
    'THURSDAY': 'Thursday',
    'FRIDAY': 'Friday',
    'SATURDAY': 'Saturday',
    'SUNDAY': 'Sunday',
    // Also handle numeric values (0-6)
    '0': 'Sunday',
    '1': 'Monday',
    '2': 'Tuesday',
    '3': 'Wednesday',
    '4': 'Thursday',
    '5': 'Friday',
    '6': 'Saturday',
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
  };
  return dayNames[day] || day;
};

function formatTimeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

export default function OptimizationEngine() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  // State
  const [recommendations, setRecommendations] = useState([]);
  const [displayedRecs, setDisplayedRecs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cachedAt, setCachedAt] = useState(null);
  const [applying, setApplying] = useState({});
  const [activeStatus, setActiveStatus] = useState('active'); // active | completed | dismissed
  const [dismissedIds, setDismissedIds] = useState([]);
  const [completedIds, setCompletedIds] = useState([]);
  const [completedHistory, setCompletedHistory] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [targetInfo, setTargetInfo] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Pagination - show limited items, load more on action
  const ITEMS_PER_PAGE = 25;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load recommendations
  useEffect(() => {
    loadRecommendations();
    loadTargetInfo();
    trackOnboardingMilestone('run_analysis');
  }, []);

  const loadTargetInfo = async () => {
    try {
      const response = await apiCall('/api/optimization/resolved-targets');
      const data = await response.json();
      if (data.success) {
        setTargetInfo(data.targets);
      }
    } catch (e) {
      console.error('Error loading targets:', e);
    }
  };

  const loadRecommendations = async (forceRefresh = false) => {
    const hasExistingData = recommendations.length > 0;
    
    if (hasExistingData && !forceRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const recsUrl = forceRefresh 
        ? '/api/optimization/all-recommendations?refresh=true' 
        : '/api/optimization/all-recommendations';
      const [recsResponse, dismissedResponse, completedResponse] = await Promise.all([
        apiCall(recsUrl, { timeout: 90000 }),
        apiCall('/api/optimization/dismissed'),
        apiCall('/api/optimization/completed')
      ]);
      
      const recsData = await recsResponse.json();
      const dismissedData = await dismissedResponse.json();
      const completedData = await completedResponse.json();
      
      if (recsData.success) {
        // Use backend ranking directly (includes diversity interleaving)
        setRecommendations(recsData.recommendations || []);
        setDismissedIds(dismissedData.dismissed || []);
        setCompletedIds(completedData.completed || []);
        setCompletedHistory(completedData.history || []);
        
        if (recsData.fromCache && recsData.cachedAt) {
          setCachedAt(recsData.cachedAt);
        } else {
          setCachedAt(Date.now());
        }
        
        if (forceRefresh) {
          showSuccess('Recommendations refreshed with latest data');
        }
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Filter recommendations by status
  const getFilteredRecs = useCallback(() => {
    if (activeStatus === 'dismissed') {
      return recommendations.filter(r => dismissedIds.includes(r.id));
    }
    if (activeStatus === 'completed') {
      // Show recommendations that match current active recs AND are completed
      const matchedRecs = recommendations.filter(r => completedIds.includes(r.id));
      const matchedIds = new Set(matchedRecs.map(r => r.id));
      
      // Also include history items that are no longer in active recommendations
      const historyOnlyRecs = completedHistory
        .filter(h => !matchedIds.has(h.id))
        .map(h => ({
          id: h.id,
          type: h.type,
          subType: h.subType || h.actionType,
          title: h.title || `Applied: ${h.subType || h.actionType}`,
          description: h.reasoning || `Applied ${h.actionType} optimization`,
          impact: h.impact || '',
          severity: 'medium',
          confidence: 0.5,
          appliedAt: h.appliedAt,
          campaignId: h.campaignId,
          adGroupId: h.adGroupId,
          isHistoryOnly: true
        }));
      
      return [...matchedRecs, ...historyOnlyRecs];
    }
    // Active = not dismissed and not completed
    return recommendations.filter(r => !dismissedIds.includes(r.id) && !completedIds.includes(r.id));
  }, [recommendations, dismissedIds, completedIds, completedHistory, activeStatus]);

  // Group by improvement type
  const groupByType = useCallback((recs) => {
    const groups = {};
    recs.forEach(rec => {
      const type = rec.subType || rec.type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(rec);
    });
    return groups;
  }, []);

  // Apply optimization
  const applyOptimization = async (recommendation) => {
    setApplying(prev => ({ ...prev, [recommendation.id]: true }));
    
    try {
      const response = await apiCall('/api/optimization/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recommendation }),
        timeout: 60000
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Track in database for persistence
        try {
          await apiCall('/api/optimization/track-applied', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ recommendation, accountId: recommendation.campaignId })
          });
        } catch (trackErr) {
          console.warn('Could not track applied recommendation:', trackErr);
        }
        // Move to completed
        setCompletedIds(prev => [...prev, recommendation.id]);
        setExpandedId(null);
        showSuccess('Optimization applied successfully!');
      } else {
        showError(data.error || 'Failed to apply optimization');
      }
    } catch (error) {
      showError(error.message || 'An error occurred while applying optimization');
    } finally {
      setApplying(prev => ({ ...prev, [recommendation.id]: false }));
    }
  };

  // Dismiss recommendation
  const dismissRecommendation = async (recommendation, duration = 'forever') => {
    try {
      const response = await apiCall('/api/optimization/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recommendation, duration })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDismissedIds(prev => [...prev, recommendation.id]);
        setExpandedId(null);
      }
    } catch (error) {
      console.error('Error dismissing:', error);
    }
  };

  // Undo applied recommendation
  const undoRecommendation = async (recommendation) => {
    setApplying(prev => ({ ...prev, [recommendation.id]: true }));
    
    try {
      const response = await apiCall('/api/optimization/undo-applied', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recommendation })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Remove from completed
        setCompletedIds(prev => prev.filter(id => id !== recommendation.id));
        setCompletedHistory(prev => prev.filter(h => h.id !== recommendation.id));
        showSuccess(data.message || 'Improvement undone successfully');
      } else {
        showError(data.error || 'Failed to undo improvement');
      }
    } catch (error) {
      showError(error.message || 'An error occurred while undoing the improvement');
    } finally {
      setApplying(prev => ({ ...prev, [recommendation.id]: false }));
    }
  };

  // Get type display info
  const getTypeInfo = (type) => {
    const types = {
      // Bid optimizations
      'increase_bid': { label: 'Increase Keyword Bid', icon: TrendingUp, color: '#10b981' },
      'decrease_bid': { label: 'Decrease Keyword Bid', icon: TrendingDown, color: '#f59e0b' },
      'pause_low_performer': { label: 'Pause Keyword', icon: Pause, color: '#ef4444' },
      'first_page_bid': { label: 'Fix First Page Bid', icon: Target, color: '#3b82f6' },
      'replace_broad': { label: 'Replace Broad Match', icon: Target, color: '#8b5cf6' },
      'improve_quality_score': { label: 'Improve Quality Score', icon: Award, color: '#f97316' },
      
      // Search term optimizations
      'promote_search_term': { label: 'Promote Search Term', icon: Plus, color: '#10b981' },
      'negate_search_term': { label: 'Negative Search Term', icon: MinusCircle, color: '#ef4444' },
      
      // Negative keywords
      'add_negative_campaign': { label: 'Add N-Gram Negative', icon: XCircle, color: '#ef4444' },
      'add_negative_account': { label: 'Add Account Negative', icon: XCircle, color: '#ef4444' },
      'negative_keyword': { label: 'Add Negative Keyword', icon: XCircle, color: '#ef4444' },
      
      // Device adjustments
      'increase_device_bid': { label: 'Increase Device Bid', icon: Smartphone, color: '#10b981' },
      'decrease_device_bid': { label: 'Decrease Device Bid', icon: Smartphone, color: '#f59e0b' },
      'exclude_device': { label: 'Exclude Device', icon: Smartphone, color: '#ef4444' },
      
      // Day parting
      'increase_day_bid': { label: 'Increase Day Bid', icon: Calendar, color: '#10b981' },
      'decrease_day_bid': { label: 'Decrease Day Bid', icon: Calendar, color: '#f59e0b' },
      'reduce_day_bid': { label: 'Reduce Day Spend', icon: Calendar, color: '#ef4444' },
      
      // Geographic
      'increase_geo_bid': { label: 'Increase Location Bid', icon: MapPin, color: '#10b981' },
      'decrease_geo_bid': { label: 'Decrease Location Bid', icon: MapPin, color: '#f59e0b' },
      'exclude_geo': { label: 'Exclude Location', icon: MapPin, color: '#ef4444' },
      
      // Audience
      'increase_audience_bid': { label: 'Increase Audience Bid', icon: Users, color: '#10b981' },
      'decrease_audience_bid': { label: 'Decrease Audience Bid', icon: Users, color: '#f59e0b' },
      'exclude_audience': { label: 'Exclude Audience', icon: Users, color: '#ef4444' },
      
      // Budget & Strategy
      'increase_budget': { label: 'Increase Budget', icon: DollarSign, color: '#10b981' },
      'decrease_budget': { label: 'Decrease Budget', icon: DollarSign, color: '#f59e0b' },
      'pause_campaign': { label: 'Pause Campaign', icon: Pause, color: '#ef4444' },
      'reallocate_budget': { label: 'Reallocate Budget', icon: DollarSign, color: '#8b5cf6' },
      'switch_to_smart_bidding': { label: 'Switch to Smart Bidding', icon: Zap, color: '#6366f1' },
      'increase_target_cpa': { label: 'Adjust Target CPA', icon: Target, color: '#f59e0b' },
      'decrease_target_cpa': { label: 'Lower Target CPA', icon: Target, color: '#10b981' },
      'switch_to_roas': { label: 'Switch to ROAS Bidding', icon: TrendingUp, color: '#6366f1' },
      'upgrade_bidding': { label: 'Upgrade Bidding Strategy', icon: Zap, color: '#6366f1' },
      'enable_ecpc': { label: 'Enable Enhanced CPC', icon: Zap, color: '#3b82f6' },
      
      // Ads
      'pause_losing_ad': { label: 'Pause Underperforming Ad', icon: Pause, color: '#ef4444' },
      
      // Network
      'disable_search_partners': { label: 'Disable Search Partners', icon: Wifi, color: '#f59e0b' },
      'disable_content_network': { label: 'Disable Display Network', icon: Wifi, color: '#f59e0b' },

      // Google native recommendations (kept types)
      'KEYWORD': { label: 'Add Keyword (Google)', icon: Plus, color: '#4285f4' },
      'CAMPAIGN_BUDGET': { label: 'Raise Budget (Google)', icon: DollarSign, color: '#4285f4' },
      'FORECASTING_CAMPAIGN_BUDGET': { label: 'Seasonal Budget (Google)', icon: DollarSign, color: '#4285f4' },
      'MOVE_UNUSED_BUDGET': { label: 'Move Unused Budget (Google)', icon: DollarSign, color: '#4285f4' },
      'TARGET_CPA_OPT_IN': { label: 'Target CPA Bidding (Google)', icon: Target, color: '#4285f4' },
      'TARGET_ROAS_OPT_IN': { label: 'Target ROAS Bidding (Google)', icon: Target, color: '#4285f4' },
      'MAXIMIZE_CONVERSIONS_OPT_IN': { label: 'Maximize Conversions (Google)', icon: Zap, color: '#4285f4' },
      'RESPONSIVE_SEARCH_AD': { label: 'Add Search Ad (Google)', icon: Plus, color: '#4285f4' },
      'RESPONSIVE_SEARCH_AD_ASSET': { label: 'Add Ad Assets (Google)', icon: Plus, color: '#4285f4' },
      'RESPONSIVE_SEARCH_AD_IMPROVE_AD_STRENGTH': { label: 'Improve Ad Strength (Google)', icon: Award, color: '#4285f4' },
      'SITELINK_ASSET': { label: 'Add Sitelinks (Google)', icon: Plus, color: '#4285f4' },
      'CALLOUT_ASSET': { label: 'Add Callouts (Google)', icon: Plus, color: '#4285f4' },
      'IMPROVE_GOOGLE_TAG_COVERAGE': { label: 'Improve Tag Coverage (Google)', icon: Target, color: '#4285f4' },
    };
    
    // Fallback: prettify raw enum-style types (e.g. RAISE_TARGET_CPA -> "Raise Target Cpa")
    const prettify = (t) => String(t || '')
      .split('_')
      .map(w => w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w)
      .join(' ');
    return types[type] || { label: prettify(type), icon: Zap, color: '#6b7280' };
  };

  // Render
  const filteredRecs = getFilteredRecs();
  const groupedRecs = groupByType(filteredRecs.slice(0, visibleCount));
  const totalActive = recommendations.filter(r => !dismissedIds.includes(r.id) && !completedIds.includes(r.id)).length;
  const totalCompleted = completedIds.length;
  const totalDismissed = dismissedIds.length;

  // Group recommendations by priority for display
  const highPriorityRecs = filteredRecs.filter(r => r.severity === 'high').slice(0, Math.min(10, visibleCount));
  const mediumPriorityRecs = filteredRecs.filter(r => r.severity === 'medium').slice(0, Math.min(15, visibleCount));
  const lowPriorityRecs = filteredRecs.filter(r => r.severity === 'low').slice(0, Math.min(10, visibleCount));

  return (
    <div style={{ 
      padding: isMobile ? '20px 16px' : '40px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      color: theme.colors.text.primary 
    }}>
      {/* Header */}
      <div style={{ marginBottom: isMobile ? '20px' : '32px' }}>
        <h1 style={{ 
          fontSize: isMobile ? '22px' : '28px', 
          fontWeight: '700', 
          marginBottom: '8px',
          color: theme.colors.text.primary 
        }}>
          Improvements
        </h1>
        
        {/* Status Tabs */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '12px' : '24px',
          marginTop: isMobile ? '16px' : '20px',
          overflowX: 'auto',
          paddingBottom: '4px'
        }}>
          <button
            onClick={() => setActiveStatus('active')}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 0',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              color: activeStatus === 'active' ? theme.colors.brand.primary : theme.colors.text.secondary,
              borderBottom: activeStatus === 'active' ? `2px solid ${theme.colors.brand.primary}` : '2px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            Active <span style={{ 
              marginLeft: '6px',
              padding: '2px 8px',
              background: activeStatus === 'active' ? theme.colors.brand.primary : theme.colors.bg.tertiary,
              color: activeStatus === 'active' ? 'white' : theme.colors.text.secondary,
              borderRadius: '10px',
              fontSize: '13px'
            }}>{totalActive}</span>
          </button>
          
          <button
            onClick={() => setActiveStatus('completed')}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 0',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              color: activeStatus === 'completed' ? theme.colors.brand.primary : theme.colors.text.secondary,
              borderBottom: activeStatus === 'completed' ? `2px solid ${theme.colors.brand.primary}` : '2px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            Completed <span style={{ 
              marginLeft: '6px',
              padding: '2px 8px',
              background: activeStatus === 'completed' ? theme.colors.brand.primary : theme.colors.bg.tertiary,
              color: activeStatus === 'completed' ? 'white' : theme.colors.text.secondary,
              borderRadius: '10px',
              fontSize: '13px'
            }}>{totalCompleted}</span>
          </button>
          
          <button
            onClick={() => setActiveStatus('dismissed')}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 0',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              color: activeStatus === 'dismissed' ? theme.colors.brand.primary : theme.colors.text.secondary,
              borderBottom: activeStatus === 'dismissed' ? `2px solid ${theme.colors.brand.primary}` : '2px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            Dismissed <span style={{ 
              marginLeft: '6px',
              padding: '2px 8px',
              background: activeStatus === 'dismissed' ? theme.colors.brand.primary : theme.colors.bg.tertiary,
              color: activeStatus === 'dismissed' ? 'white' : theme.colors.text.secondary,
              borderRadius: '10px',
              fontSize: '13px'
            }}>{totalDismissed}</span>
          </button>
          
          <div style={{ flex: 1 }} />
          
          {cachedAt && !isLoading && (
            <span style={{ 
              fontSize: '12px', 
              color: theme.colors.text.tertiary,
              marginRight: '8px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {isRefreshing ? 'Updating...' : `Updated ${formatTimeAgo(cachedAt)}`}
            </span>
          )}
          
          <button
            onClick={() => loadRecommendations(true)}
            disabled={isLoading || isRefreshing}
            title="Force refresh from Google Ads (bypasses cache)"
            style={{
              background: theme.colors.bg.tertiary,
              border: `1px solid ${theme.colors.border.primary}`,
              color: theme.colors.text.primary,
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: (isLoading || isRefreshing) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RefreshCw size={16} className={(isLoading || isRefreshing) ? 'animate-spin' : ''} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Target Info Banner - always visible */}
      {targetInfo && (
        <div style={{
          background: targetInfo.isDefault 
            ? 'rgba(245, 158, 11, 0.1)' 
            : 'rgba(99, 102, 241, 0.08)',
          border: `1px solid ${targetInfo.isDefault 
            ? 'rgba(245, 158, 11, 0.3)' 
            : 'rgba(99, 102, 241, 0.2)'}`,
          borderRadius: '12px',
          padding: '14px 20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {targetInfo.isDefault ? (
            <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
          ) : (
            <Target size={20} style={{ color: '#6366f1' }} />
          )}
          <div style={{ flex: 1 }}>
            {targetInfo.isDefault && (
              <div style={{ fontWeight: '600', color: '#f59e0b', marginBottom: '4px', fontSize: '14px' }}>
                Using Default Targets
              </div>
            )}
            <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
              Target CPA: €{targetInfo.cpa?.toFixed(2)} | Target ROAS: {targetInfo.roas?.toFixed(1)}x
              <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                ({targetInfo.source?.replace('_', ' ')})
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'optimization' } })}
            style={{
              background: targetInfo.isDefault ? '#f59e0b' : 'rgba(99, 102, 241, 0.15)',
              border: 'none',
              color: targetInfo.isDefault ? 'white' : '#6366f1',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '0.85';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '1';
            }}
          >
            <Settings size={16} />
            {targetInfo.isDefault ? 'Configure Targets' : 'Edit Targets'}
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          color: theme.colors.text.secondary
        }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: theme.colors.text.primary,
            marginBottom: '8px'
          }}>
            Analyzing your account in the background
          </div>
          <div style={{
            maxWidth: '520px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            We are checking bids, search terms, budgets, and campaign settings. Larger accounts can take a little longer,
            but you can keep using Bidoryx while this runs. Recommendations will appear automatically as soon as they are ready.
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredRecs.length === 0 && (
        <div style={{
          background: theme.colors.bg.secondary,
          borderRadius: '16px',
          padding: '60px',
          textAlign: 'center',
          border: `1px solid ${theme.colors.border.primary}`
        }}>
          <CheckCircle size={48} style={{ color: '#10b981', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
            {activeStatus === 'active' ? 'All caught up!' : `No ${activeStatus} improvements`}
          </h3>
          <p style={{ color: theme.colors.text.secondary }}>
            {activeStatus === 'active' 
              ? 'No new improvements at this time. Check back later!'
              : `You haven't ${activeStatus} any improvements yet.`
            }
          </p>
        </div>
      )}

      {/* Recommendations by Priority */}
      {!isLoading && activeStatus === 'active' && filteredRecs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* HIGH PRIORITY */}
          {highPriorityRecs.length > 0 && (
            <PrioritySection
              title="High Priority"
              count={highPriorityRecs.length}
              color="#ef4444"
              recommendations={highPriorityRecs}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              applying={applying}
              onApply={applyOptimization}
              onDismiss={dismissRecommendation}
              getTypeInfo={getTypeInfo}
              theme={theme}
            />
          )}
          
          {/* MEDIUM PRIORITY */}
          {mediumPriorityRecs.length > 0 && (
            <PrioritySection
              title="Medium Priority"
              count={mediumPriorityRecs.length}
              color="#f59e0b"
              recommendations={mediumPriorityRecs}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              applying={applying}
              onApply={applyOptimization}
              onDismiss={dismissRecommendation}
              getTypeInfo={getTypeInfo}
              theme={theme}
            />
          )}
          
          {/* LOW PRIORITY */}
          {lowPriorityRecs.length > 0 && (
            <PrioritySection
              title="Low Priority"
              count={lowPriorityRecs.length}
              color="#10b981"
              recommendations={lowPriorityRecs}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              applying={applying}
              onApply={applyOptimization}
              onDismiss={dismissRecommendation}
              getTypeInfo={getTypeInfo}
              theme={theme}
            />
          )}
          
          {/* Load More */}
          {filteredRecs.length > visibleCount && (
            <button
              onClick={() => setVisibleCount(prev => prev + ITEMS_PER_PAGE)}
              style={{
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                color: theme.colors.text.primary,
                padding: '16px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                textAlign: 'center'
              }}
            >
              Load More ({filteredRecs.length - visibleCount} remaining)
            </button>
          )}
        </div>
      )}

      {/* Dismissed/Completed list - simpler view */}
      {!isLoading && activeStatus !== 'active' && filteredRecs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredRecs.slice(0, visibleCount).map(rec => {
            const typeInfo = getTypeInfo(rec.subType || rec.type);
            const Icon = typeInfo.icon;
            
            return (
              <div
                key={rec.id}
                style={{
                  background: theme.colors.bg.secondary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: '12px',
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  opacity: 0.7
                }}
              >
                <Icon size={20} style={{ color: typeInfo.color }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {rec.title || typeInfo.label}
                  </div>
                  <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
                    {rec.campaign || rec.keyword || typeInfo.label}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {rec.appliedAt && (
                    <div style={{ fontSize: '12px', color: theme.colors.text.tertiary }}>
                      {new Date(rec.appliedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                  {activeStatus === 'completed' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); undoRecommendation(rec); }}
                      disabled={applying[rec.id]}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        color: theme.colors.text.secondary,
                        background: theme.colors.bg.tertiary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: '6px',
                        cursor: applying[rec.id] ? 'wait' : 'pointer',
                        opacity: applying[rec.id] ? 0.6 : 1,
                        transition: 'all 0.15s ease'
                      }}
                      title="Ongedaan maken"
                    >
                      <RotateCcw size={12} />
                      {applying[rec.id] ? '...' : 'Undo'}
                    </button>
                  )}
                  <div style={{ 
                    fontSize: '13px', 
                    color: activeStatus === 'completed' ? '#10b981' : theme.colors.text.tertiary,
                    padding: '4px 12px',
                    background: activeStatus === 'completed' ? 'rgba(16,185,129,0.1)' : theme.colors.bg.tertiary,
                    borderRadius: '6px'
                  }}>
                    {activeStatus === 'completed' ? '✓ Applied' : 'Dismissed'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CSS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}

// ============================================
// PRIORITY SECTION COMPONENT
// ============================================

function PrioritySection({ 
  title, 
  count, 
  color, 
  recommendations, 
  expandedId, 
  setExpandedId,
  applying,
  onApply,
  onDismiss,
  getTypeInfo,
  theme
}) {
  return (
    <div>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px'
      }}>
        <span style={{
          background: color,
          color: 'white',
          padding: '4px 12px',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '700'
        }}>
          {title}
        </span>
        <span style={{
          color: theme.colors.text.secondary,
          fontSize: '14px'
        }}>
          {count} Improvements
        </span>
      </div>
      
      {/* Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {recommendations.map(rec => (
          <ImprovementCard
            key={rec.id}
            recommendation={rec}
            isExpanded={expandedId === rec.id}
            isApplying={applying[rec.id]}
            onToggle={() => setExpandedId(expandedId === rec.id ? null : rec.id)}
            onApply={() => onApply(rec)}
            onDismiss={(duration) => onDismiss(rec, duration)}
            getTypeInfo={getTypeInfo}
            theme={theme}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================
// IMPROVEMENT CARD COMPONENT (Opteo-style)
// ============================================

function ImprovementCard({
  recommendation,
  isExpanded,
  isApplying,
  onToggle,
  onApply,
  onDismiss,
  getTypeInfo,
  theme
}) {
  const typeInfo = getTypeInfo(recommendation.subType || recommendation.type);
  const Icon = typeInfo.icon;
  const [showDismissOptions, setShowDismissOptions] = useState(false);
  const dismissRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dismissRef.current && !dismissRef.current.contains(event.target)) {
        setShowDismissOptions(false);
      }
    };

    if (showDismissOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDismissOptions]);

  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: '12px',
      overflow: 'visible',
      transition: 'all 0.2s'
    }}>
      {/* Card Header - Opteo Style */}
      <div
        style={{
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          cursor: 'pointer',
          position: 'relative',
          zIndex: showDismissOptions ? 100 : 1
        }}
        onClick={onToggle}
      >
        {/* Plus/Checkbox Icon */}
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          border: `2px solid ${theme.colors.border.primary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <span style={{ color: theme.colors.text.tertiary, fontSize: '18px', lineHeight: 1 }}>+</span>
        </div>
        
        {/* Action Label */}
        <div style={{ 
          fontWeight: '600', 
          fontSize: '16px',
          color: theme.colors.text.primary,
          minWidth: '180px',
          flexShrink: 0
        }}>
          {typeInfo.label}
        </div>
        
        {/* Tags - C, A, K badges (Opteo style) */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'nowrap',
          gap: '6px',
          flex: 1,
          alignItems: 'center',
          overflow: 'hidden'
        }}>
          {recommendation.campaign && (
            <span style={{
              background: `${typeInfo.color}15`,
              border: `1px solid ${typeInfo.color}40`,
              padding: '4px 10px',
              borderRadius: '16px',
              color: theme.colors.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '200px',
              flexShrink: 1
            }}>
              <span style={{ 
                background: typeInfo.color, 
                color: 'white', 
                fontWeight: '700',
                width: '18px',
                height: '18px',
                minWidth: '18px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px'
              }}>C</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{recommendation.campaign}</span>
            </span>
          )}
          {recommendation.adGroup && (
            <span style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              padding: '4px 10px',
              borderRadius: '16px',
              color: theme.colors.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '180px',
              flexShrink: 1
            }}>
              <span style={{ 
                background: '#f59e0b', 
                color: 'white', 
                fontWeight: '700',
                width: '18px',
                height: '18px',
                minWidth: '18px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px'
              }}>A</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{recommendation.adGroup}</span>
            </span>
          )}
          {recommendation.keyword && (
            <span style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              padding: '4px 10px',
              borderRadius: '16px',
              color: theme.colors.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '160px',
              flexShrink: 1
            }}>
              <span style={{ 
                background: '#10b981', 
                color: 'white', 
                fontWeight: '700',
                width: '18px',
                height: '18px',
                minWidth: '18px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px'
              }}>K</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{recommendation.keyword}</span>
            </span>
          )}
          {recommendation.device && (
            <span style={{
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.4)',
              padding: '4px 10px',
              borderRadius: '16px',
              color: theme.colors.text.primary,
              fontSize: '12px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              {recommendation.device}
            </span>
          )}
          {recommendation.dayOfWeek && (
            <span style={{
              background: 'rgba(236, 72, 153, 0.1)',
              border: '1px solid rgba(236, 72, 153, 0.4)',
              padding: '4px 10px',
              borderRadius: '16px',
              color: theme.colors.text.primary,
              fontSize: '12px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              {formatDayName(recommendation.dayOfWeek)}
            </span>
          )}
          {recommendation.searchTerm && (
            <span style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.4)',
              padding: '4px 10px',
              borderRadius: '16px',
              color: theme.colors.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '150px',
              flexShrink: 1
            }}>
              <span style={{ 
                background: '#8b5cf6', 
                color: 'white', 
                fontWeight: '700',
                width: '18px',
                height: '18px',
                minWidth: '18px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px'
              }}>S</span>
              {recommendation.searchTerm}
            </span>
          )}
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', zIndex: 10 }}>
          <div ref={dismissRef} style={{ position: 'relative', zIndex: showDismissOptions ? 9999 : 1 }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDismissOptions(!showDismissOptions);
              }}
              style={{
                background: theme.colors.bg.tertiary,
                border: `1px solid ${theme.colors.border.primary}`,
                color: theme.colors.text.secondary,
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Dismiss
              <ChevronDown size={14} />
            </button>
            
            {/* Dismiss Options Dropdown */}
            {showDismissOptions && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: theme.colors.bg.primary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: '8px',
                padding: '8px',
                zIndex: 9999,
                minWidth: '140px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)'
              }}>
                {['1_week', '1_month', '3_months', 'forever'].map(duration => (
                  <button
                    key={duration}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(duration);
                      setShowDismissOptions(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      color: theme.colors.text.primary,
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => e.target.style.background = theme.colors.bg.tertiary}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    {duration === 'forever' ? 'Forever' : duration.replace('_', ' ')}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            style={{
              background: theme.colors.brand.primary,
              border: 'none',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Open Improvement
          </button>
        </div>
        
        {/* Expand indicator */}
        <ChevronRight 
          size={20} 
          style={{ 
            color: theme.colors.text.tertiary,
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }} 
        />
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div style={{
          padding: '0 20px 20px',
          borderTop: `1px solid ${theme.colors.border.primary}`
        }}>
          <div style={{ paddingTop: '20px' }}>
            {/* Description */}
            {recommendation.description && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: theme.colors.text.tertiary,
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Recommendation
                </div>
                <div style={{ color: theme.colors.text.primary, lineHeight: 1.6 }}>
                  {recommendation.description}
                </div>
              </div>
            )}
            
            {/* Reasoning */}
            {recommendation.reasoning && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: theme.colors.text.tertiary,
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Why This Matters
                </div>
                <div style={{ color: theme.colors.text.secondary, fontSize: '14px', lineHeight: 1.6 }}>
                  {recommendation.reasoning}
                </div>
              </div>
            )}
            
            {/* Impact */}
            {recommendation.impact && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: theme.colors.text.tertiary,
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Expected Impact
                </div>
                <div style={{ 
                  color: '#10b981', 
                  fontWeight: '600',
                  fontSize: '15px'
                }}>
                  {recommendation.impact}
                </div>
              </div>
            )}
            
            {/* Details Grid */}
            {recommendation.details && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px',
                marginBottom: '20px'
              }}>
                {Object.entries(recommendation.details).filter(([key]) => 
                  !['reasoning_type'].includes(key)
                ).slice(0, 6).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      background: theme.colors.bg.tertiary,
                      padding: '12px 16px',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ 
                      fontSize: '11px', 
                      color: theme.colors.text.tertiary,
                      marginBottom: '4px',
                      textTransform: 'uppercase'
                    }}>
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div style={{ 
                      fontWeight: '600',
                      color: theme.colors.text.primary
                    }}>
                      {typeof value === 'number' 
                        ? value.toLocaleString() 
                        : Array.isArray(value) 
                          ? value.map(v => typeof v === 'object' ? (v.name || JSON.stringify(v)) : v).join(', ')
                          : typeof value === 'object' && value !== null
                            ? (value.name || JSON.stringify(value))
                            : String(value)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Advisory note (for recommendations that can't be auto-applied) */}
            {recommendation.actionable === false && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                background: theme.colors.bg.tertiary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '16px'
              }}>
                <Lightbulb size={18} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '1px' }} />
                <div style={{ fontSize: '13px', color: theme.colors.text.secondary, lineHeight: 1.5 }}>
                  This is an <strong>advisory</strong> recommendation. There's no single change to auto-apply — review it and adjust the relevant settings (targets, ad copy, landing page, or budget allocation) directly in Google Ads.
                </div>
              </div>
            )}

            {/* Limited-data caveat (statistical significance) */}
            {recommendation.actionable !== false && (recommendation.lowData || recommendation.significance === 'directional') && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: theme.colors.text.tertiary,
                marginBottom: '16px'
              }}>
                <Info size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                {recommendation.significance === 'directional'
                  ? 'Directional — based on limited conversion data. Treat as a hypothesis to test.'
                  : 'Limited data — low confidence. Consider waiting for more clicks/conversions.'}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {recommendation.actionable === false ? (
                <button
                  onClick={() => onDismiss('forever')}
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    border: 'none',
                    color: 'white',
                    padding: '14px 28px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <CheckCircle size={18} />
                  Mark as reviewed
                </button>
              ) : (
                <>
                  <button
                    onClick={onApply}
                    disabled={isApplying}
                    style={{
                      background: isApplying 
                        ? theme.colors.bg.tertiary 
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      color: 'white',
                      padding: '14px 28px',
                      borderRadius: '10px',
                      cursor: isApplying ? 'not-allowed' : 'pointer',
                      fontSize: '15px',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isApplying ? (
                      <>
                        <RefreshCw size={18} className="animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Apply Improvement
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => onDismiss('forever')}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${theme.colors.border.primary}`,
                      color: theme.colors.text.secondary,
                      padding: '14px 28px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <XCircle size={18} />
                    Dismiss
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
